import React from "react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Plus, Minus, Info } from "lucide-react";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";

interface MetricSliderProps {
  label: string;
  value: number;             // e.g. 0.70 if isPercentage=true
  onChange: (value: number) => void;
  min: number;               // e.g. 0 if we want to let user type "0%" min
  max: number;               // e.g. 100 if we want to let user type "100%" max
  weight: string;
  unit?: string;
  isPercentage?: boolean;
  description?: string;
}

/**
 * If isPercentage=true, we treat the "value" as a decimal (0.70 for 70%).
 * We display 70 in the box, but store 0.70 internally.
 */
export const MetricSlider: React.FC<MetricSliderProps> = ({
  label,
  value,
  onChange,
  min,
  max,
  weight,
  unit,
  isPercentage,
  description
}) => {
  // If it's a percentage, forcibly treat the internal range as [0..1].
  // Otherwise just use the numeric range from props.
  const effectiveMin = isPercentage ? 0 : min;
  const effectiveMax = isPercentage ? 1 : max;

  // Determine if this is the peer assessment metric
  const isPeerAssessment = label.toLowerCase().includes("peer assessment");
  
  // Decide step size based on metric type
  const effectiveStep = isPercentage 
    ? 0.01 
    : isPeerAssessment 
      ? 0.1 
      : 1;

  // We keep an internal state that starts from the parent's "value"
  const [internalValue, setInternalValue] = React.useState(value);
  
  // Format display value based on metric type
  const formatDisplayValue = (val: number) => {
    if (isPercentage) {
      return Math.round(val * 100).toString();
    }
    if (isPeerAssessment) {
      return val.toFixed(1);
    }
    return val.toString();
  };

  // Keep track of display value
  const [inputValue, setInputValue] = React.useState(formatDisplayValue(value));

  React.useEffect(() => {
    setInternalValue(value);
    setInputValue(formatDisplayValue(value));
  }, [value]);

  const handleChange = (rawInput: number) => {
    // If user typed "85", we want to store 0.85 for percentages
    const newVal = isPercentage ? rawInput / 100 : rawInput;
    // clamp it to [effectiveMin..effectiveMax]
    const clamped = Math.min(Math.max(newVal, effectiveMin), effectiveMax);
    // round to nearest effectiveStep
    const rounded = Math.round(clamped / effectiveStep) * effectiveStep;

    setInternalValue(rounded);
    setInputValue(formatDisplayValue(rounded));
    onChange(rounded);
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const inputText = e.target.value;
    setInputValue(inputText); // Always update the display value

    const typed = parseFloat(inputText);
    if (!isNaN(typed)) {
      // For non-percentage values, only update if it's within range
      if (!isPercentage && (typed < min || typed > max)) {
        return;
      }
      handleChange(typed);
    }
  };

  const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    const typed = parseFloat(e.target.value);
    if (isNaN(typed)) {
      // Reset to internal value if invalid
      setInputValue(formatDisplayValue(internalValue));
    } else {
      // Clamp the value on blur
      const clamped = Math.min(Math.max(typed, isPercentage ? 0 : min), isPercentage ? 100 : max);
      const rounded = Math.round(clamped / effectiveStep) * effectiveStep;
      handleChange(rounded);
      setInputValue(formatDisplayValue(rounded));
    }
  };

  const incrementValue = () => {
    const newVal = Math.min(internalValue + effectiveStep, effectiveMax);
    const rounded = Math.round(newVal / effectiveStep) * effectiveStep;
    setInternalValue(rounded);
    setInputValue(formatDisplayValue(rounded));
    onChange(rounded);
  };

  const decrementValue = () => {
    const newVal = Math.max(internalValue - effectiveStep, effectiveMin);
    const rounded = Math.round(newVal / effectiveStep) * effectiveStep;
    setInternalValue(rounded);
    setInputValue(formatDisplayValue(rounded));
    onChange(rounded);
  };

  return (
    <div className="py-0.5">
      <div className="grid grid-cols-[1fr_200px] gap-2 items-center">
        <div className="flex items-center gap-1">
          <label className="text-sm font-medium text-gray-700">{label}</label>
          <div className="flex items-center gap-0.5">
            <span className="text-sm text-gray-500">({weight})</span>
            {description && (
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Info className="h-3 w-3 text-[#0057B7] cursor-help" />
                  </TooltipTrigger>
                  <TooltipContent className="bg-[#0057B7] text-white border-none rounded-[10px]">
                    <p className="text-sm">{description}</p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            )}
          </div>
        </div>

        <div className="flex items-center gap-0.5 justify-end">
          <Button
            type="button"
            variant="outline"
            size="icon"
            className="h-6 w-6 shrink-0"
            onClick={decrementValue}
            disabled={internalValue <= effectiveMin}
          >
            <Minus className="h-3 w-3" />
          </Button>

          <Input
            type="number"
            value={inputValue}
            onChange={handleInputChange}
            onBlur={handleBlur}
            min={isPercentage ? 0 : min}
            max={isPercentage ? 100 : max}
            step={effectiveStep}
            className="w-[80px] h-6 text-center text-sm font-medium 
                       [appearance:textfield] 
                       [&::-webkit-outer-spin-button]:appearance-none 
                       [&::-webkit-inner-spin-button]:appearance-none"
          />

          <Button
            type="button"
            variant="outline"
            size="icon"
            className="h-6 w-6 shrink-0"
            onClick={incrementValue}
            disabled={internalValue >= effectiveMax}
          >
            <Plus className="h-3 w-3" />
          </Button>
        </div>
      </div>
    </div>
  );
};